import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EmployeeService } from '../../../../employee.service';
import { ExternalStaffService } from '../../../../services/external-staff.service';
import { HelperService } from '../../../../services/helper.service';
import { LogbookService } from '../../../../services/logbook.service';
import { FirebaseStorageService } from '../../../../services/firebase-storage.service';
import { AuthorizationService } from '../../../../services/authorization.service';
import { finalize } from 'rxjs/operators';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { NbDialogService } from '@nebular/theme';
import { LogbooksAuthorizationComponentComponent } from '../../../../authorizations/logbooks-authorization-module/logbooks-authorization-component/logbooks-authorization-component.component';
import { NewEmployeeComponent } from '../../../../new-employee/new-employee.component';
import * as moment from 'moment';

interface Logbook {
  stationId?: string;
  type: any;
  activity_type: string;
  date: any;
  dateFormat: string;
  start_time: string;
  end_time: string;
  image: any;
  responsable: string;
  fields?: any;
  external_staff: {
    provider: string;
    responsable: string;
  };
}

interface LogbookTemplate {
  id: number;
  name: string;
  data: Logbook;
  logbook_type: string;
  station_id: string;
  created_at: string;
  updated_at: string;
}

@Component({
  selector: 'ngx-extinguisher',
  templateUrl: './extinguisher.component.html',
  styleUrls: ['./extinguisher.component.scss'],
})
export class ExtinguisherComponent implements OnInit {
  @Input() stationId: string;
  @Output() refreshLogbook: EventEmitter<any> = new EventEmitter();

  templates: LogbookTemplate[] = [];
  templateSelected: LogbookTemplate = null;

  constructor(
    private employeeService: EmployeeService,
    private externalStaffService: ExternalStaffService,
    private helperService: HelperService,
    private logbookService: LogbookService,
    private firebaseStorageService: FirebaseStorageService,
    private authorizationService: AuthorizationService,
    private dialogService: NbDialogService,
  ) { }

  // Start Attributes
  loading = false;
  employees = [];
  externalStaff = [];
  images = [];
  uploadPercent: any;
  downloadURLs = [];
  authorizations = [];
  uploads = [];
  logbook: Logbook = {
    type: 9,
    activity_type: '',
    dateFormat: '',
    date: new Date(),
    start_time: moment().format('H:mm'),
    end_time: moment().format('H:mm'),
    image: '',
    responsable: '',
    external_staff: {
      provider: '',
      responsable: ''
    },
    fields: {
      authorization_id: '',
      extinguisher_number: '',
      location: '',
      capacity: '',
      correct_recharge: false,
      responsable_charge: '',
      observations: '',
      upload_date: new Date(),
      next_load_date: new Date()
    },
  };
  responsableOptions = [
    {
      name: 'Responsable de la estación de servicio',
      value: 1,
    },
    {
      name: 'Área de mantenimiento',
      value: 2,
    },
    {
      name: 'Responsable Técnico',
      value: 3,
    },
    {
      name: 'Responsable Personal de Mantenimiento',
      value: 4,
    },
    {
      name: 'Encargado de la estación de servicio',
      value: 5,
    },
  ];

  attributes = [
    {
      id: 1,
      label: 'Señalizado',
      input: 'signal',
      value: false,
    },
    {
      id: 2,
      label: 'Funda',
      input: 'sheath',
      value: false,
    },
    {
      id: 3,
      label: 'Manometro',
      input: 'pressure-gauge',
      value: false,
    },
    {
      id: 4,
      label: 'Manija de carga y de disparo',
      input: 'loading-firing-handle',
      value: false,
    },
    {
      id: 5,
      label: 'Etiqueta',
      input: 'label',
      value: false,
    },
    {
      id: 6,
      label: 'Seguro y sello',
      input: 'safe-seal',
      value: false,
    },
    {
      id: 7,
      label: 'Collarín',
      input: 'collar',
      value: false,
    },
  ];
  // End Attributes

  async ngOnInit() {
    await this.getEmployeesByStation();
    this.getExternalStaffStation()
    this.getAuthorizations();
    this.getTemplates();
  }

  async getAuthorizations() {
    this.loading = true;
    const [err, response]: any = await this.authorizationService.index(this.stationId);
    this.loading = false;
    if (err) return this.alert('Oops,', 'error', 'Intenta más tarde [Error. Lista de Autorizaciones]');
    this.authorizations = response.data;
  }

  async getEmployeesByStation() {
    const [err, result] = await this.employeeService.getEmployees(this.stationId);
    if (err) return this.alert('Oops, intenta más tarde [Error. Lista empleados]', 'error');
    this.employees = result.data;
  }

  async loadImage(event: any) {
    if (!event.target.files.length) return;
    for await (const image of event.target.files) {
      const reader = new FileReader();
      await reader.readAsDataURL(image);
      reader.onload = (e: any) => {
        image.imageBase64 = e.target.result;
        this.images.push(image);
      };
    }
  }

  async getExternalStaffStation() {
    this.loading = true;
    const [err, response]: any = await this.externalStaffService.getExternalStaffStation(this.stationId);
    this.loading = false;
    if (err) return this.alert('Oops, intenta más tarde [Error. Lista proveedores]', 'error');
    this.externalStaff = response.data;
  }

  openModal(isEdit = false) {
    this.dialogService.open(LogbooksAuthorizationComponentComponent, {
      hasScroll: true,
      context: {
        stationId: this.stationId,
        isEdit,
      },
    }).onClose.subscribe((modified: boolean) => modified ? this.getAuthorizations() : console.error('no modificaron nada'));
  }

  openNewEmployee() {
    this.dialogService.open(NewEmployeeComponent, {
      hasScroll: true,
      context: {
        stationId: this.stationId,
      },
    }).onClose.subscribe(employeeStored => employeeStored ? this.getEmployeesByStation() : null);
  }

  validateForm() {
    this.store()
  }

  async store() {
    const validDataHasEmptyFields = Object.keys(this.logbook).includes('');
    const validFieldsHasEmptyFields = Object.keys(this.logbook.fields).includes('');
    if (validDataHasEmptyFields || validFieldsHasEmptyFields) return this.alert('Ingresa todos los datos necesiarios', 'error');
    this.loading = true;
    this.logbook.fields.attributes = this.attributes;
    this.logbook.fields.next_load_date = moment(this.logbook.fields.next_load_date).format('YYYY-MM-DD');
    this.logbook.fields.upload_date = moment(this.logbook.fields.upload_date).format('YYYY-MM-DD');
    const [error, result]: any = await this.logbookService.store(this.stationId, this.logbook);
    const logbookId = result.data.id;
    if (error) {
      this.loading = false;
      return this.alert('Oops, intenta más tarde', 'error');
    }

    if (!this.images.length) {
      this.loading = false;
      this.refreshLogbook.emit(true);
      this.logbookService.exitCreateLogbook();
      return this.alert('Bitácora creada con éxito', 'success');
    }
    await this.firebaseStorageService.setPath('images/logbooks');
    this.uploads = [];
    this.downloadURLs = [];
    for await (const [index, file] of this.images.entries()) {
      const filename = `${logbookId}_${index}`;
      const fileRef = await this.firebaseStorageService.referenceCloudStorage(filename);
      const task = this.firebaseStorageService.taskCloudStorage(filename, file);

      // observe percentage changes
      this.uploadPercent = task.percentageChanges();

      // get notified when the download URL is available
      task.snapshotChanges().pipe(
        finalize(() => {
          fileRef.getDownloadURL().subscribe(async (url) => {
            this.downloadURLs = this.downloadURLs.concat([url]);
            if (this.images.length === this.downloadURLs.length) {
              const firstURL = this.downloadURLs[0];
              this.downloadURLs.shift();
              const [err] = await this.logbookService.updateUrlImage(logbookId, firstURL, this.downloadURLs);
              if (err) return this.alert('Oops, ocurrio un error al cargar el archivo', 'error');
              this.refreshLogbook.emit(true);
              this.logbookService.exitCreateLogbook()
              this.alert('Bitácora creada con éxito', 'success');
              this.loading = false;
            }
          });
        }),
      ).subscribe();
    }
  }

  async delete(index: number) {
    this.images.splice(index, 1);
  }

  async validateExternalStaff() {
    this.logbook.external_staff = {
      provider: '',
      responsable: '',
    };
    if (this.logbook.responsable !== 'otro') return;
  }

  /**
   * Show sweert alert
   * @param title
   * @param type
   * @param text
   */
  async alert(title: string = 'asdasd', type: SweetAlertIcon = 'success', text: string = '') {
    await Swal.fire({
      icon: type,
      title: title,
      text: text,
    });
  }


  async getTemplates() {
    const [error, response] = await this.logbookService.getTemplates(this.stationId, this.logbook.type);
    if (error) return this.alert('Oops, ocurrio un error al cargar las plantillas', 'error');
    this.templates = response;
  }

  saveTemplate(e) {
    e.preventDefault();

    Swal.fire({
      title: '¿Deseas guardar esta bitácora como plantilla?',
      showCancelButton: true,
      confirmButtonText: `Guardar`,
      cancelButtonText: `Cancelar`,
      html: `<input type="text" id="templateName" class="swal2-input" placeholder="Nombre de plantilla">`,
      preConfirm: () => {
        // @ts-ignore
        const templateName: any = Swal.getPopup().querySelector('#templateName').value;
        if (!templateName) {
          Swal.showValidationMessage(`Ingresa un nombre para la plantilla`);
        }
        return { templateName };
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        await this.storeTemplate(result.value.templateName);
        /*const [err, result] = await this.logbookService.saveTemplate(this.stationId, this.logbook);
        if (err) return this.alert('Oops, intenta más tarde [Error. Guardar plantilla]', 'error');
        this.alert('Plantilla guardada con éxito', 'success');
        */
      } else if (result.isDenied) {
        /*
        this.alert('Plantilla no guardada', 'info');
        */
      }
    });
  }

  async storeTemplate(templateName: string) {
    const validDataHasEmptyFields = Object.keys(this.logbook).includes('');
    const validFieldsHasEmptyFields = Object.keys(this.logbook.fields).includes('');
    if (validDataHasEmptyFields || validFieldsHasEmptyFields) return this.alert('Ingresa todos los datos necesiarios', 'error');
    this.loading = true;
    this.logbook.fields.attributes = this.attributes;
    this.logbook.fields.next_load_date = moment(this.logbook.fields.next_load_date).format('YYYY-MM-DD');
    this.logbook.fields.upload_date = moment(this.logbook.fields.upload_date).format('YYYY-MM-DD');
    this.logbook.stationId = this.stationId;

    await this.logbookService.storeTemplate(this.logbook, templateName);

    this.loading = false;
  }

  setTemplate() {
    this.logbook = this.templateSelected.data;
    this.logbook.date = new Date();
    this.attributes = this.logbook.fields.attributes;
  }

  setGoogleTemplate() {
    this.logbook = {
      type: 9,
      activity_type: '',
      dateFormat: '',
      date: new Date(),
      start_time: moment().format('H:mm'),
      end_time: moment().format('H:mm'),
      image: '',
      responsable: '',
      external_staff: {
        provider: '',
        responsable: ''
      },
    };
  }
}
